import type React from 'react'
import { useCallback, useEffect, useRef } from 'react'
import { FieldCoordinateSystem } from '@/utils'
import type { Drawing } from '@/types'
import type { SnapTarget } from '@/utils/Drawing.utils'
import { findAllSnapTargets } from '@/utils/Drawing.utils'
import { PLAYER_RADIUS_FEET } from '@/constants'
import { useTheme } from '../../contexts/SettingsContext'
import { useControlPointDrag } from './hooks'
import { DrawingSnapIndicator } from './indicators/DrawingSnapIndicator'
import { PlayerSnapIndicator } from './indicators/PlayerSnapIndicator'

interface ControlPointOverlayProps {
	drawing: Drawing | null
	drawings: Drawing[]
	players?: Array<{
		id: string
		x: number
		y: number
		label: string
		color: string
	}>
	coordSystem: FieldCoordinateSystem
	snapThreshold: number
	isGlobalSelect?: boolean
	zoom?: number
	panX?: number
	panY?: number
	onDragPoint?: (
		_drawingId: string,
		_pointId: string,
		_x: number,
		_y: number,
	) => void
	onMerge?: (
		_sourceDrawingId: string,
		_sourcePointId: string,
		_targetDrawingId: string,
		_targetPointId: string,
	) => void
	onLinkToPlayer?: (
		_drawingId: string,
		_pointId: string,
		_playerId: string,
	) => void
}

interface DragState {
	pointId: string
	snapTarget?: SnapTarget | null
}

/**
 * Renders draggable control points and snap indicators.
 */
export function ControlPointOverlay({
										drawing,
										drawings,
										players,
										coordSystem,
										snapThreshold,
										isGlobalSelect = false,
										onDragPoint,
										onMerge,
										onLinkToPlayer,
										zoom = 1,
										panX = 0,
										panY = 0,
									}: ControlPointOverlayProps) {
	// Call all hooks BEFORE any conditional returns
	const { theme: _theme } = useTheme()
	const overlayRef = useRef<SVGGElement | null>(null)

	// Drag state management with refs and window event listener
	const {
		dragState,
		snapTarget,
		playerSnapTarget,
		setDragState,
		setSnapTarget,
		setPlayerSnapTarget,
		snapTargetRef,
		playerSnapTargetRef,
		cleanup,
	} = useControlPointDrag<DragState>({
		onPointerUp: ({
						  dragState: currentDragState,
						  snapTarget: currentSnapTarget,
						  playerSnapTarget: currentPlayerSnapTarget,
					  }) => {
			if (!currentDragState || !drawing) {
				return
			}

			if (currentPlayerSnapTarget && currentDragState && onLinkToPlayer) {
				onLinkToPlayer(
					drawing.id,
					currentDragState.pointId,
					currentPlayerSnapTarget.playerId,
				)
			} else if (currentSnapTarget && currentDragState && onMerge) {
				onMerge(
					drawing.id,
					currentDragState.pointId,
					currentSnapTarget.drawingId,
					currentSnapTarget.pointId,
				)
			}

			cleanup()
		},
	})

	// Track pointer movement to update dragged point and snap indicators
	const handlePointerMove = useCallback(
		function handlePointerMove(event: PointerEvent) {
			if (!dragState || !onDragPoint || !drawing) return
			const svg = overlayRef.current?.ownerSVGElement
			if (!svg) return

			const rect = svg.getBoundingClientRect()
			const screenX = event.clientX - rect.left
			const screenY = event.clientY - rect.top
			const feet = coordSystem.screenToFeet(screenX, screenY, zoom, panX, panY)

			onDragPoint(drawing.id, dragState.pointId, feet.x, feet.y)

			// Use unified snap target detection
			const thresholdFeet = snapThreshold / coordSystem.scale
			const snapResult = findAllSnapTargets({
				position: feet,
				players,
				drawings,
				excludeDrawingId: drawing.id,
				playerRadiusFeet: PLAYER_RADIUS_FEET,
				drawingThresholdFeet: thresholdFeet,
			})

			// Update state with snap results (refs auto-synced by hook)
			setPlayerSnapTarget(snapResult.playerSnapTarget)
			setSnapTarget(snapResult.drawingSnapTarget)
		},
		[
			dragState,
			onDragPoint,
			drawing,
			coordSystem,
			drawings,
			players,
			snapThreshold,
			zoom,
			panX,
			panY,
		],
	)

	useEffect(() => {
		if (!dragState) return
		window.addEventListener('pointermove', handlePointerMove)
		return () => window.removeEventListener('pointermove', handlePointerMove)
	}, [dragState, handlePointerMove])

	// Early return AFTER all hooks
	if (!drawing) return null

	function startDrag(pointId: string) {
		return (event: React.PointerEvent) => {
			event.stopPropagation()
			setDragState({ pointId })
		}
	}

	function handlePointerUpLocal() {
		// Window handler performs linking logic - this is just for cleanup
		// when pointer is released directly on the overlay
		cleanup()
	}

	return (
		<g
			ref={overlayRef}
			pointerEvents={isGlobalSelect ? 'none' : 'visiblePainted'}
			onPointerUp={handlePointerUpLocal}
		>
			{/* Iterate over point pool instead of nested segments */}
			{Object.values(drawing.points).map((point) => {
				// Hide the specific point that's linked to player
				if (drawing.playerId && drawing.linkedPointId == point.id) {
					return null
				}

				const pixel = coordSystem.feetToPixels(point.x, point.y)
				// Control node colors from CSS variables
				const computedStyle = getComputedStyle(document.documentElement)
				const nodeFill = computedStyle.getPropertyValue('--control-point-fill').trim() || 'white'
				const nodeStroke = computedStyle.getPropertyValue('--control-point-stroke').trim() || 'black'

				return (
					<circle
						key={point.id}
						cx={pixel.x}
						cy={pixel.y}
						r={6}
						fill={nodeFill}
						stroke={nodeStroke}
						strokeWidth={2}
						pointerEvents={isGlobalSelect ? 'none' : 'all'}
						style={
							isGlobalSelect
								? undefined
								: { cursor: dragState ? 'grabbing' : 'grab' }
						}
						onPointerDown={
							isGlobalSelect ? undefined : startDrag(point.id)
						}
					/>
				)
			})}
			{!isGlobalSelect && snapTarget && (
				<DrawingSnapIndicator
					coordSystem={coordSystem}
					snapPoint={snapTarget.point}
				/>
			)}
			{!isGlobalSelect && playerSnapTarget && (
				<PlayerSnapIndicator
					coordSystem={coordSystem}
					playerPosition={playerSnapTarget.point}
				/>
			)}
		</g>
	)
}

